import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface SimphonyGen1Service {
  apiEndpointUrl: string;
  simphonyVersion: string;
  menuLanguage: string;
  employeeRef: number | null;
  orderTypeRef: number | null;
  tenderMediaRef: number | null;
  revenueCenterRefs: (number | null)[];
}

export const MENU_LANGUAGE = /^[a-z]{2}-[A-Z]{2}$/;
export const SIMPHONY_VERSION = /^\d+\.\d+\.\d+$/;

const SERVICE = 'currentConfig.service';

const isRef = (value: number | null) => Number.isInteger(value);

const defaultConfig = (): IntegrationConfig => ({
  service: {
    apiEndpointUrl: '',
    simphonyVersion: '',
    menuLanguage: '',
    employeeRef: null,
    orderTypeRef: null,
    tenderMediaRef: null,
    revenueCenterRefs: [],
  },
  features: {},
  cron: [],
  webhooks: [],
});

export const simphonyGen1: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig
      .service as unknown as SimphonyGen1Service;

    test(`${SERVICE}.apiEndpointUrl`, t('validations.required'), () => {
      enforce(service.apiEndpointUrl).isNotBlank();
    });

    omitWhen(!service.apiEndpointUrl, () => {
      test(`${SERVICE}.apiEndpointUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiEndpointUrl)).isTruthy();
      });
    });

    test(`${SERVICE}.simphonyVersion`, t('validations.required'), () => {
      enforce(service.simphonyVersion).isNotBlank();
    });

    omitWhen(!service.simphonyVersion, () => {
      test(`${SERVICE}.simphonyVersion`, t('validations.invalid'), () => {
        enforce(service.simphonyVersion).matches(SIMPHONY_VERSION);
      });
    });

    test(`${SERVICE}.menuLanguage`, t('validations.required'), () => {
      enforce(service.menuLanguage).isNotBlank();
    });

    omitWhen(!service.menuLanguage, () => {
      test(`${SERVICE}.menuLanguage`, t('validations.invalid'), () => {
        enforce(service.menuLanguage).matches(MENU_LANGUAGE);
      });
    });

    for (const field of [
      'employeeRef',
      'orderTypeRef',
      'tenderMediaRef',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(isRef(service[field])).isTruthy();
      });
    }

    test(`${SERVICE}.revenueCenterRefs`, t('validations.required'), () => {
      const refs = Array.isArray(service.revenueCenterRefs)
        ? service.revenueCenterRefs
        : [];

      enforce(refs).isNotEmpty();
      enforce(refs.every(isRef)).isTruthy();
    });
  },
};
